'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, ArrowLeft } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { Button } from '@/shared/components/Button';
import { Input } from '@/shared/components/Input';
import { Label } from '@/shared/components/Label';
import { useAuth } from '@/features/auth';
import {
	forgotPasswordSchema,
	type ForgotPasswordFormData,
} from '@/features/auth/validation/schemas';
import { cn } from '@/shared/lib/utils';
import { logger } from '@/core/logger';

export interface ForgotPasswordFormProps {
	className?: string;
}

export function ForgotPasswordForm({ className }: ForgotPasswordFormProps) {
	const [error, setError] = useState<string>('');
	const [isSubmitted, setIsSubmitted] = useState(false);
	const { resetPassword } = useAuth();
	const router = useRouter();

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
		getValues,
	} = useForm<ForgotPasswordFormData>({
		resolver: zodResolver(forgotPasswordSchema),
	});

	const onSubmit = async (data: ForgotPasswordFormData) => {
		try {
			setError('');
			await resetPassword(data);
			logger.info('Password reset email sent successfully');
			setIsSubmitted(true);
		} catch (error) {
			// Server action error already logged on server with appropriate level
			// Log user action outcome for client-side monitoring
			logger.warn('Password reset request failed');
			setError(
				error instanceof Error
					? error.message
					: 'An unexpected error occurred. Please try again.'
			);
		}
	};

	const isFormLoading = isSubmitting;

	if (isSubmitted) {
		return (
			<div className={cn('text-center space-y-4', className)}>
				<div className="glass-light rounded-2xl p-6 border border-green-400/30">
					<h3 className="text-lg font-semibold text-green-400 mb-2 drop-shadow-md">
						Check your email
					</h3>
					<p className="text-sm text-white/70">
						We&apos;ve sent a password reset link to{' '}
						<span className="font-medium text-white">
							{getValues('email')}
						</span>
					</p>
				</div>

				<div className="text-sm text-white/50 space-y-2">
					<p>
						Didn&apos;t receive the email? Check your spam folder or
						try again in a few minutes.
					</p>
				</div>

				<div className="space-y-3">
					<Button
						type="button"
						className="glass-light glass-hover w-full border border-white/20 text-white"
						onClick={() => setIsSubmitted(false)}
					>
						Try again
					</Button>

					<Button
						type="button"
						className="glass-frosted glass-hover w-full border border-white/20 text-white/80"
						onClick={() => router.push('/login')}
					>
						<ArrowLeft className="mr-2 h-4 w-4" />
						Back to sign in
					</Button>
				</div>
			</div>
		);
	}

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			className={cn('space-y-5', className)}
			noValidate
		>
			<div className="text-center space-y-2 mb-6">
				<h2 className="text-3xl font-light text-white drop-shadow-lg">
					Reset your password
				</h2>
				<p className="text-sm text-white/60 drop-shadow-md">
					Enter your email address and we&apos;ll send you a link to
					reset your password.
				</p>
			</div>

			{error && (
				<div
					className="glass-light rounded-xl p-3 text-sm text-red-400 border border-red-400/30"
					role="alert"
					aria-live="polite"
				>
					{error}
				</div>
			)}

			<div className="space-y-3">
				<Label htmlFor="email" className="text-white/90">
					Email
				</Label>
				<Input
					id="email"
					type="email"
					autoComplete="email"
					placeholder="Enter your email address"
					disabled={isFormLoading}
					aria-invalid={!!errors.email}
					aria-describedby={errors.email ? 'email-error' : undefined}
					className="glass-light border-white/20 text-white placeholder:text-white/40"
					{...register('email')}
				/>
				{errors.email && (
					<p id="email-error" className="text-sm text-red-400">
						{errors.email.message}
					</p>
				)}
			</div>

			<Button
				type="submit"
				className="glass-frosted glass-hover w-full border border-white/20 bg-gradient-to-r from-red-500/80 to-red-600/80 text-white font-semibold shadow-lg shadow-red-500/20"
				disabled={isFormLoading}
				aria-describedby={error ? 'form-error' : undefined}
			>
				{isFormLoading && (
					<Loader2 className="mr-2 h-4 w-4 animate-spin" />
				)}
				Send reset link
			</Button>

			<Button
				type="button"
				className="glass-light glass-hover w-full border border-white/20 text-white/80"
				onClick={() => router.push('/login')}
				disabled={isFormLoading}
			>
				<ArrowLeft className="mr-2 h-4 w-4" />
				Back to sign in
			</Button>
		</form>
	);
}
